import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { FirestoreService } from 'src/app/services/firestore.service';
import { CLINICAL_TIME_DIALOG_SIZE, ClinicalTimeDialogComponent } from '../dialogs/clinical-time-dialog.component';
import { CLINICAL_TIME_LEFT_FOR_CARE_PLAN, ClinicalTimeOrigin } from '../models/ccm/ccm-pending-time.model';
import { CLINICAL_TIME_KEPT_FOR_NOTE, LinkedClinicalNote } from '../models/clinical-time-note.model';
import { ClinicalTime } from '../models/clinical.model';
import { TimeTrackingModalComponent } from '../shared/time-tracking-modal/time-tracking-modal.component';
import { TimeTrackingState } from '../shared/time-tracking-modal/time-tracking-states';
import { PatientService } from './patient.service';

@Injectable({
  providedIn: 'root',
})
export class PatientTimerService {
  currentTimerStateSubject: BehaviorSubject<any> = new BehaviorSubject(false);
  currentTimerStateObs$: Observable<boolean> = this.currentTimerStateSubject.asObservable();
  currentDisplayValuesSubject: BehaviorSubject<{ minutesSpent: number; secondsSpent: number; displayTime?: string }> =
    new BehaviorSubject({
      minutesSpent: 0,
      secondsSpent: 0,
      displayTime: '00:00',
    });
  currentDisplayValuesObs$: Observable<{ minutesSpent: number; secondsSpent: number; displayTime?: string }> =
    this.currentDisplayValuesSubject.asObservable();
  /** Whether the timer's time is waiting on a note, for the note panel to say so. */
  timeAfterNotePending$: Observable<boolean>;
  private maxTimeInSeconds = 1200; // 20 minutes
  private maxTimeCounter = 1;
  private oneMinuteInSeconds = 60;
  private minutesSpent = 0;
  private secondsSpent = 0;
  private timeSpent = 0;
  private isExtraSmall: Observable<BreakpointState> = this.breakpointObserver.observe(Breakpoints.XSmall);
  private initialDisplay = '00:00';
  private timerWorker: Worker;
  private timerSubject: Subject<number> = new Subject<number>();
  private clearTimer$: Subject<boolean> = new Subject<boolean>();
  private timerObs$: Observable<number> = this.timerSubject.asObservable();
  private reachedMaxTimeInSeconds = false;
  private isSecondComplete = 'tick';
  /**
   * Set when the nurse asked to save the timer's time at a clinic with CCM care plan time on: that time
   * comes from a note, so the timer keeps running and the next saved note opens the time form, which
   * then cannot be closed without saving.
   */
  private timeAfterNotePendingSubject = new BehaviorSubject<boolean>(false);

  constructor(
    private fs: FirestoreService,
    private router: Router,
    private dialog: MatDialog,
    private readonly breakpointObserver: BreakpointObserver,
    private patientService: PatientService
  ) {
    this.timeAfterNotePending$ = this.timeAfterNotePendingSubject.asObservable();
    this.timerWorker = this.createWorker();
    this.timerWorker.onmessage = (event) => {
      if (event.data === this.isSecondComplete) {
        this.timerSubject.next(this.timeSpent + 1);
      }
    };
  }

  getOneMinuteInSeconds() {
    return this.oneMinuteInSeconds;
  }

  initializeTimer() {
    this.timerWorker.postMessage('start');
    this.clearTimer$ = new Subject<boolean>();
    this.timerObs$.pipe(takeUntil(this.clearTimer$)).subscribe((time) => {
      this.timeSpent = time;
      this.checkIfTimerIsCompleted(this.timeSpent);
    });
    this.currentTimerStateSubject.next(true);
  }

  resumeTimer() {
    this.timerWorker.postMessage('start');
    this.currentTimerStateSubject.next(true);
  }

  pauseTimer() {
    this.currentTimerStateSubject.next(false);
    this.timerWorker.postMessage('stop');
  }

  stopTimer() {
    this.generalStopTimer();
    this.currentDisplayValuesSubject.next({ minutesSpent: 0, secondsSpent: 0, displayTime: '00:00' });
  }

  stopByMaxTime() {
    this.generalStopTimer();
    this.currentDisplayValuesSubject.next({ minutesSpent: 0, secondsSpent: 0, displayTime: 'Stopped' });
  }

  resetTimer() {
    this.clearTimer();
    this.initializeTimer();
  }

  clearTimer() {
    this.currentTimerStateSubject?.next(false);
    this.clearTimer$.next(true);
    this.clearTimer$.complete();
    this.timerWorker.postMessage('stop');
    this.timeSpent = 0;
    this.minutesSpent = 0;
    this.secondsSpent = 0;
    this.maxTimeCounter = 1;
    this.reachedMaxTimeInSeconds = false;
  }

  displayTime() {
    return `${this.minutesSpent?.toString().padStart(2, '0')}:${this.secondsSpent?.toString().padStart(2, '0')}`;
  }

  getMinutesSpent() {
    return this.minutesSpent;
  }

  getSecondsSpent() {
    return this.secondsSpent;
  }

  /** Total elapsed seconds tracked for the current patient (used to preserve time on a chart jump). */
  getElapsedSeconds(): number {
    return this.timeSpent;
  }

  /**
   * Seeds the timer with a previously preserved elapsed value (e.g. when restoring a chart draft).
   * The chart entry resolver normally has the worker running already; this updates the display
   * immediately and keeps counting from `seconds`. If the worker is not currently running it is
   * resumed (guarded, so it never double-starts the interval).
   */
  seedTimer(seconds: number): void {
    if (!seconds || seconds <= 0) {
      return;
    }
    this.timeSpent = seconds;
    this.minutesSpent = Math.floor(seconds / this.oneMinuteInSeconds);
    this.secondsSpent = seconds - this.minutesSpent * this.oneMinuteInSeconds;
    this.currentDisplayValuesSubject.next({
      minutesSpent: this.minutesSpent,
      secondsSpent: this.secondsSpent,
      displayTime: this.displayTime(),
    });
    if (!this.currentTimerStateSubject.value) {
      this.resumeTimer();
    }
  }

  isTimmerCleared() {
    return this.displayTime() === this.initialDisplay;
  }

  openClinicalTime(category?: string) {
    const dialogRef = this.openClinicalTimeDialog(this.timerEntry(category), true, undefined, undefined, 'timer');
    dialogRef.afterClosed().subscribe((result) => this.handleTimeDialogClosed('timer', result));
  }

  /**
   * The time form a saved clinical note opens, with the note it covers. The timer stops while the form is
   * open. Saved, the timer clears; cancelled, for every clinic, a timer that was running carries on and a
   * paused one stays paused with its minutes.
   * When the nurse got here by asking to save the timer's time at a clinic where time needs a note, the
   * form cannot be closed without saving: the note was written for that time.
   */
  openClinicalTimeFromNote(category?: string, linkedNote?: LinkedClinicalNote): void {
    const wasRunning = this.currentTimerStateSubject.value === true;
    const required = this.timeAfterNotePendingSubject.value;
    this.timeAfterNotePendingSubject.next(false);
    this.stopTimer();
    const dialogRef = this.openClinicalTimeDialog(
      this.timerEntry(category),
      true,
      undefined,
      undefined,
      'timer',
      linkedNote,
      required
    );
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true || result === CLINICAL_TIME_LEFT_FOR_CARE_PLAN) {
        this.clearTimer();
      } else if (wasRunning) {
        this.resumeTimer();
      } else {
        // A paused timer stays paused with its minutes; stopping it for the form showed 00:00.
        this.currentDisplayValuesSubject.next({
          minutesSpent: this.minutesSpent,
          secondsSpent: this.secondsSpent,
          displayTime: this.displayTime(),
        });
      }
    });
  }

  /**
   * At a clinic with CCM care plan time on, saving the timer's time starts with the note: the timer keeps
   * running and the note panel opens. The time form follows once the note is saved.
   */
  requestTimeFromNote(): void {
    this.timeAfterNotePendingSubject.next(true);
    if (!this.patientService.creatingNote) {
      this.patientService.toggleNoteView();
    }
  }

  /** The note panel closed without a saved note: the time it was opened for is no longer waiting on it. */
  cancelTimeFromNote(): void {
    this.timeAfterNotePendingSubject.next(false);
  }

  openClinicalTimeDialog(
    time?: ClinicalTime,
    isNew?: boolean,
    id?: string,
    programValue?: string,
    origin?: ClinicalTimeOrigin,
    linkedNote?: LinkedClinicalNote,
    required = false
  ): MatDialogRef<ClinicalTimeDialogComponent> {
    const data: Record<string, unknown> = time
      ? {
          time: {
            minutes: time.minutes,
            mins: time.minutes,
            secs: time['seconds'],
            categories: time.categories ?? {},
            category: time.category,
            timeStamp: time.timeStamp,
            user_id: time.user_id,
            patient_id: this.patientService.currentPatientService.user_id,
            excluded: time.excluded,
            contact: time.contact,
            contact_status: time.contact_status,
            require_provider_intervention: time.require_provider_intervention,
            device_troubleshooting: time.device_troubleshooting,
          },
          isNew,
          id,
          origin,
        }
      : { time: { programValue, patient_id: this.patientService.currentPatientService.user_id }, isNew, origin };
    if (linkedNote) {
      data.linkedNote = linkedNote;
    }
    if (required) {
      data.required = true;
    }
    return this.openTimeDialog(data);
  }

  /**
   * The time form's dialog with the data it is given, e.g. an entry restored after building a care plan.
   * A form that must be saved (`data.required`) cannot be closed with Escape or a click outside.
   */
  openTimeDialog(data: Record<string, unknown>): MatDialogRef<ClinicalTimeDialogComponent> {
    const dialogRef = this.dialog.open(ClinicalTimeDialogComponent, {
      ...CLINICAL_TIME_DIALOG_SIZE,
      maxWidth: '100vw',
      data,
      disableClose: data.required === true,
    });

    this.isExtraSmall.subscribe((size) => {
      if (size.matches) {
        dialogRef.updateSize('100vw', '100vh');
      }
    });
    return dialogRef;
  }

  /**
   * What closing the time form does to the chart timer, by opener, on any close: the timer and the summary
   * clear it, an edit leaves it alone. Also when the nurse leaves to build a care plan (CCM care plan time,
   * S2.4): that time is counted by the waiting entry, so the timer cannot save the same minutes again.
   * Except a form cancelled at a clinic where time needs a note: the timer keeps its minutes for the note.
   */
  handleTimeDialogClosed(origin: ClinicalTimeOrigin, result?: unknown): void {
    if (result === CLINICAL_TIME_KEPT_FOR_NOTE) {
      return;
    }
    if (origin !== 'edit') {
      this.clearTimer();
    }
  }

  openTimeTrackingModal(currentValue?: number): MatDialogRef<TimeTrackingModalComponent> {
    const modal = this.dialog.open(TimeTrackingModalComponent, {
      maxWidth: '100vw',
      data: {
        title: `Confirm that you are still tracking patient time`,
        body: 'Are you still tracking patient time?',
        patient: this.patientService.currentPatientService,
        timeSpent: currentValue ?? this.timeSpent,
      },
      disableClose: true,
    });
    this.isExtraSmall.subscribe((size) => {
      if (size.matches) {
        modal.updateSize('100vw', '100vh');
      }
    });
    return modal;
  }

  confirmIfUsersWantToLeave(isCounting: boolean): Observable<boolean> {
    if (this.reachedMaxTimeInSeconds || (!isCounting && this.isTimmerCleared())) {
      this.reachedMaxTimeInSeconds = false;
      return of(true);
    } else {
      const modal = this.openTimeTrackingModal();
      return this.setTimeTrackingAfterCloseListener(modal);
    }
  }

  setTimeTrackingAfterCloseListener(modal: MatDialogRef<TimeTrackingModalComponent>): Observable<boolean> {
    return modal.afterClosed().pipe(
      take(1),
      map((resp) => {
        if (resp === TimeTrackingState.KEEP_TRACKING) {
          this.resumeTimer();
          return false;
        } else if (resp === TimeTrackingState.WRITE_NOTE) {
          // The nurse stays on the chart, still timing, to write the note the time will be saved from.
          this.resumeTimer();
          this.requestTimeFromNote();
          return false;
        } else if (resp === TimeTrackingState.SAVE_TIME) {
          this.clearTimer();
          this.currentDisplayValuesSubject.next({ minutesSpent: 0, secondsSpent: 0, displayTime: '00:00' });
          return false;
        } else {
          return true;
        }
      })
    );
  }

  /** A new entry with the timer's minutes, as the time form opens with it. */
  private timerEntry(category?: string): ClinicalTime {
    return {
      category: category ?? 'rpms',
      minutes: this.getMinutesToSave(),
      seconds: this.getSecondsToSave(),
      timeStamp: new Date(),
      user_id: this.patientService.currentPatientService?.user_id,
      excluded: false,
      require_provider_intervention: false,
      device_troubleshooting: false,
    };
  }

  private generalStopTimer() {
    this.currentTimerStateSubject.next(false);
    this.timerWorker.postMessage('stop');
  }

  private checkIfTimerIsCompleted(currentValue: number): void {
    this.minutesSpent = Math.floor(currentValue / this.oneMinuteInSeconds);
    this.secondsSpent = currentValue - this.minutesSpent * this.oneMinuteInSeconds;
    this.currentDisplayValuesSubject.next({
      minutesSpent: this.minutesSpent,
      secondsSpent: this.secondsSpent,
      displayTime: this.displayTime(),
    });
    if (currentValue % (this.maxTimeInSeconds * this.maxTimeCounter) === 0 && currentValue > 0) {
      this.stopByMaxTime();
      const modal = this.openTimeTrackingModal(currentValue);
      this.setTimeTrackingAfterCloseListener(modal).subscribe((flag) => {
        if (flag) {
          this.reachedMaxTimeInSeconds = true;
          this.router.navigateByUrl('clinical');
        }
        this.maxTimeCounter++;
      });
    }
  }

  private getMinutesToSave(): number {
    return this.timeSpent > this.oneMinuteInSeconds
      ? (this.timeSpent - (this.timeSpent % this.oneMinuteInSeconds)) / this.oneMinuteInSeconds
      : 0;
  }

  private getSecondsToSave(): number {
    return this.timeSpent % this.oneMinuteInSeconds;
  }

  private createWorker(): Worker {
    return new Worker(
      URL.createObjectURL(
        new Blob(
          [
            `
        let timer;

        const startTimer = () => {
          timer = setInterval(() => {
            postMessage('tick');
          }, 1000);
        };

        const stopTimer = () => {
          clearInterval(timer);
        };

        onmessage = (event) => {
          if (event.data === 'start') {
            startTimer();
          } else if (event.data === 'stop') {
            stopTimer();
          }
        };
      `,
          ],
          { type: 'text/javascript' }
        )
      )
    );
  }
}
